import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { API } from '../lib/api';
import { fileToCompressedDataUrl } from '../lib/imageUtils';
import { Loader2, CheckCircle2, Home as HomeIcon, Building2, Camera, X, ImagePlus } from 'lucide-react';

const CITY_OPTIONS = ['Gurgaon', 'Delhi', 'Noida', 'Bangalore', 'Mumbai', 'Pune', 'Hyderabad', 'Goa', 'Chennai', 'Kolkata', 'Other'];

const EMPTY = {
  name: '', phone: '', email: '', property_name: '', property_type: 'apartment',
  intent: 'sell', listing_category: 'residential', city: '', location: '', bhk: '',
  sqft: '', expected_price: '', message: '', images: [],
};

const MAX_IMAGES = 4;

const PostPropertyModal = ({ open, onOpenChange, defaultIntent }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, intent: defaultIntent || 'sell' });
      setSuccess(false);
    }
  }, [open, defaultIntent]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleAddFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - form.images.length;
    if (remaining <= 0) {
      toast({ title: 'Photo limit reached', description: `You can upload up to ${MAX_IMAGES} photos.` });
      return;
    }
    const accepted = files.slice(0, remaining);
    setUploading(true);
    const results = [];
    for (const f of accepted) {
      if (!f.type.startsWith('image/')) {
        toast({ title: 'Invalid file', description: `${f.name} is not an image.` });
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast({ title: 'File too large', description: `${f.name} exceeds 10MB.` });
        continue;
      }
      try {
        const dataUrl = await fileToCompressedDataUrl(f, { maxDim: 1400, quality: 0.82 });
        results.push(dataUrl);
      } catch (err) {
        toast({ title: 'Upload failed', description: err.message || 'Could not process image.' });
      }
    }
    if (results.length) {
      setForm((f) => ({ ...f, images: [...f.images, ...results].slice(0, MAX_IMAGES) }));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.city.trim()) {
      toast({ title: 'Missing details', description: 'Name, phone and city are required.' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, sqft: form.sqft ? parseInt(form.sqft) : null };
      await axios.post(`${API}/property-submissions`, payload);
      setSuccess(true);
      toast({ title: 'Property submitted!', description: 'Our team will call you within 24 hours to verify & list your property.' });
      setTimeout(() => { onOpenChange(false); }, 1800);
    } catch (err) {
      toast({ title: 'Submission failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="pp-desc">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#fef2f4] flex items-center justify-center">
              <HomeIcon className="w-5 h-5 text-[#9a1b2f]" />
            </div>
            <div>
              <DialogTitle className="text-[#9a1b2f]">Post Your Property — Free</DialogTitle>
              <DialogDescription id="pp-desc" className="text-xs text-gray-500 mt-0.5">
                List with SafeHouse in minutes. Our verification team will reach out within 24 hours.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="mt-4 font-bold text-gray-900">Property Submitted!</h3>
            <p className="text-sm text-gray-500 mt-1">Our verification team will contact you shortly.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {/* Intent tabs */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-full">
              <button type="button" onClick={() => set('intent', 'sell')} className={`h-9 rounded-full text-sm font-semibold transition-colors ${form.intent === 'sell' ? 'bg-[#9a1b2f] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                I want to Sell
              </button>
              <button type="button" onClick={() => set('intent', 'rent')} className={`h-9 rounded-full text-sm font-semibold transition-colors ${form.intent === 'rent' ? 'bg-[#9a1b2f] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                I want to Rent Out
              </button>
            </div>

            {/* Category */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { k: 'residential', icon: HomeIcon, label: 'Residential' },
                { k: 'commercial', icon: Building2, label: 'Commercial' },
              ].map((c) => {
                const active = form.listing_category === c.k;
                const Icon = c.icon;
                return (
                  <button
                    key={c.k}
                    type="button"
                    onClick={() => set('listing_category', c.k)}
                    className={`flex items-center gap-2 justify-center h-10 rounded-xl border-2 text-sm font-semibold transition-all ${active ? 'border-[#9a1b2f] bg-[#fef2f4] text-[#9a1b2f]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  >
                    <Icon className="w-4 h-4" /> {c.label}
                  </button>
                );
              })}
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="pp_name" className="text-xs">Your Name *</Label>
                <Input id="pp_name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" className="h-10" />
              </div>
              <div>
                <Label htmlFor="pp_phone" className="text-xs">Phone *</Label>
                <Input id="pp_phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91 XXXXX XXXXX" className="h-10" />
              </div>
              <div>
                <Label htmlFor="pp_email" className="text-xs">Email</Label>
                <Input id="pp_email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" className="h-10" />
              </div>
            </div>

            {/* Property details */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="pp_pname" className="text-xs">Property / Project Name</Label>
                <Input id="pp_pname" value={form.property_name} onChange={(e) => set('property_name', e.target.value)} placeholder="e.g. M3M Crown, Tower A" className="h-10" />
              </div>
              <div>
                <Label className="text-xs">Property Type</Label>
                <Select value={form.property_type} onValueChange={(v) => set('property_type', v)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {form.listing_category === 'residential' ? (
                      <>
                        <SelectItem value="apartment">Apartment</SelectItem>
                        <SelectItem value="floor">Independent Floor</SelectItem>
                        <SelectItem value="villa">Villa</SelectItem>
                        <SelectItem value="plot">Plot / Land</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="shop">Shop</SelectItem>
                        <SelectItem value="office">Office Space</SelectItem>
                        <SelectItem value="food court">Food Court</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pp_bhk" className="text-xs">Configuration</Label>
                <Input id="pp_bhk" value={form.bhk} onChange={(e) => set('bhk', e.target.value)} placeholder={form.listing_category === 'residential' ? '2 BHK / 3 BHK' : 'Retail / Office'} className="h-10" />
              </div>
              <div>
                <Label className="text-xs">City *</Label>
                <Select value={form.city} onValueChange={(v) => set('city', v)}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>
                    {CITY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pp_loc" className="text-xs">Location / Sector</Label>
                <Input id="pp_loc" value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Sector 89" className="h-10" />
              </div>
              <div>
                <Label htmlFor="pp_sqft" className="text-xs">Carpet Area (sqft)</Label>
                <Input id="pp_sqft" type="number" value={form.sqft} onChange={(e) => set('sqft', e.target.value)} placeholder="1200" className="h-10" />
              </div>
              <div>
                <Label htmlFor="pp_price" className="text-xs">Expected Price {form.intent === 'rent' ? '(per month)' : ''}</Label>
                <Input id="pp_price" value={form.expected_price} onChange={(e) => set('expected_price', e.target.value)} placeholder={form.intent === 'rent' ? '₹35,000' : '₹1.75 Cr'} className="h-10" />
              </div>
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Property Photos <span className="text-gray-400 font-normal">(max {MAX_IMAGES})</span></Label>
              <div className="mt-1.5 grid grid-cols-4 gap-2">
                {Array.from({ length: MAX_IMAGES }).map((_, i) => {
                  const img = form.images[i];
                  return (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50 group">
                      {img ? (
                        <>
                          <img src={img} alt={`Property ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(i)}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center transition-opacity"
                            aria-label={`Remove photo ${i + 1}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="w-full h-full flex flex-col items-center justify-center text-gray-400 hover:text-[#9a1b2f] hover:border-[#9a1b2f] hover:bg-[#fef2f4] transition-colors disabled:opacity-50"
                        >
                          {uploading && i === form.images.length ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <ImagePlus className="w-4 h-4" />
                              <span className="text-[10px] font-semibold mt-1">Add photo</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleAddFiles(e.target.files)}
              />
              <p className="text-[10px] text-gray-500 mt-1.5">JPG or PNG, up to 10MB each. Images are compressed automatically.</p>
            </div>

            <div>
              <Label htmlFor="pp_msg" className="text-xs">Additional Notes</Label>
              <Textarea id="pp_msg" value={form.message} onChange={(e) => set('message', e.target.value)} rows={2} placeholder="Anything else we should know..." />
            </div>

            <DialogFooter className="pt-1">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button type="submit" className="bg-[#9a1b2f] hover:bg-[#7a1524]" disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Post Property Free
              </Button>
            </DialogFooter>
            <p className="text-[10px] text-gray-500 text-center pt-1">By submitting, you agree to be contacted by SafeHouse.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PostPropertyModal;
